import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createProject,
  deleteProject,
  getEmployees,
  getProjects,
  updateProject,
} from '../services/project.service';
import { GetProjectsParams, ProjectInsertInput, ProjectUpdateInput } from '../types/project.types';

const invalidateProjectQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-projects'] });

export const useGetProjects = (params: GetProjectsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-projects', params],
    queryFn: () => getProjects(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetEmployees = (keyword: string) => {
  return useGlobalQuery({
    queryKey: ['hr-employees-lookup', keyword],
    queryFn: () => getEmployees(keyword),
    staleTime: 60 * 1000,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: ProjectInsertInput) => createProject(input),
    onSuccess: () => {
      invalidateProjectQueries(queryClient);
      toast({ title: t('PROJECT_CREATED') });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: ProjectUpdateInput }) => updateProject(params),
    onSuccess: () => {
      invalidateProjectQueries(queryClient);
      toast({ title: t('PROJECT_UPDATED') });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteProject(itemId),
    onSuccess: () => {
      invalidateProjectQueries(queryClient);
      toast({ title: t('PROJECT_DELETED') });
    },
  });
};
